import React from "react";
import {
  MantineProvider,
  Container,
  Paper,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Alert,
  Center,
} from "@mantine/core";
import { SUPPORT_EMAIL } from "../../constants/emailConstants.js";
import { IconCircleCheck, IconAlertCircle } from "@tabler/icons-react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { Footer } from "../../components/Footer/Footer";
import {
  nonProtectedRoutes,
  protectedRoutes,
} from "../../utils/nonProtectedRoutingPaths";

const theme = {};

const PaymentConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const sessionId = searchParams.get("session_id");
  const isSuccess = location.pathname.includes("/success");
  const isCancel = location.pathname.includes("/cancel");

  const handleGoToDashboard = () => {
    navigate(protectedRoutes.DASHBOARD, { relative: "route" });
  };

  const handleGoToHome = () => {
    navigate(nonProtectedRoutes.HOME);
  };

  const handleGoToCheckout = () => {
    navigate(`/app/${protectedRoutes.CHECKOUT}`, { relative: "route" });
  };

  const handleGoToContact = () => {
    navigate(nonProtectedRoutes.CONTACT);
  };

  const renderSuccessContent = () => (
    <>
      <Stack align="center" mb={30}>
        <IconCircleCheck size={80} color="green" stroke={1.5} />

        <Center>
          <Title order={1}>Welcome to Socius Legal!</Title>
        </Center>
        <Text size="lg">
          Thank you for your purchase. Your transaction has been completed.
        </Text>
      </Stack>

      <Paper withBorder radius="md" p="md" mb={30}>
        <Center>
          <Text>Session ID: {sessionId || "N/A"}</Text>
        </Center>
      </Paper>

      <Text size="sm" mb={30}>
        A confirmation email has been sent.
        <br />
        If you have any questions about your purchase, please contact our
        support team at &nbsp;
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </Text>

      <Group style={{ justifyContent: "center" }}>
        <Button color="blue" size="md" onClick={handleGoToDashboard}>
          Start Using Socius Now
        </Button>
        <Button
          variant="outline"
          color="gray"
          size="md"
          onClick={handleGoToHome}
        >
          Return to Home
        </Button>
      </Group>
    </>
  );

  const renderCancelContent = () => (
    <>
      <Stack align="center" mb={30}>
        <IconAlertCircle size={80} color="red" stroke={1.5} />
        <Center>
          <Title order={1}>Payment Canceled</Title>
        </Center>
        <Text size="lg">
          Your payment has been canceled, please select one of the following
          options to proceed
        </Text>
      </Stack>

      <Paper withBorder radius="md" p="md" mb={30}>
        <Center>
          <Text>Session ID: {sessionId || "N/A"}</Text>
        </Center>
      </Paper>

      <Group mt="md" style={{ justifyContent: "center" }}>
        <Button variant="outline" color="gray" onClick={handleGoToHome}>
          Home
        </Button>
        <Button color="blue" onClick={handleGoToCheckout}>
          Checkout
        </Button>
        <Button variant="light" color="blue" onClick={handleGoToContact}>
          Contact
        </Button>
      </Group>
    </>
  );

  const renderErrorContent = () => (
    <Alert
      icon={<IconAlertCircle size={24} />}
      title="Payment Error"
      color="red"
      radius="md"
    >
      <Text mb={15}>
        Something went wrong. You can go to the home page, retry the payment, or
        contact our support team.
      </Text>
      {sessionId && (
        <Text size="sm" mb={15}>
          Session ID: {sessionId}
        </Text>
      )}
      <Group mt="md" style={{ justifyContent: "center" }}>
        <Button variant="outline" color="gray" onClick={handleGoToHome}>
          Home
        </Button>
        <Button color="blue" onClick={handleGoToCheckout}>
          Checkout
        </Button>
        <Button variant="light" color="blue" onClick={handleGoToContact}>
          Contact
        </Button>
      </Group>
    </Alert>
  );

  const renderContent = () => {
    if (isSuccess) {
      return renderSuccessContent();
    } else if (isCancel) {
      return renderCancelContent();
    } else {
      return renderErrorContent();
    }
  };

  return (
    <MantineProvider theme={theme}>
      <Container size="md" py={60}>
        <Paper radius="md" p="xl" withBorder>
          {renderContent()}
        </Paper>
      </Container>
      <Footer />
    </MantineProvider>
  );
};

export default PaymentConfirmationPage;
